import { gql, useQuery, useSuspenseQuery } from '@apollo/client';
import CalendarToday from '@mui/icons-material/CalendarToday';
import Room from '@mui/icons-material/Room';
import { Box, CircularProgress, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Ordering } from 'gql-types';
import moment from 'moment';
import { useMemo, useState } from 'react';

import { AXLE_TEAL } from '../../constants/colors';
import { UNASSIGNED_QHP_ID } from '../../constants/visit';
import { GraphInputHelpers } from '../../fetching/graphql/GraphInputHelpers';
import { useDashboardPageContext } from '../../pages/DashboardPage/DashboardPageContext';
import {
    ClinicianAppointmentOutcome,
    ClinicianAppointmentState,
} from '../../types/clinicianAppointment';
import { VisitOutcome, VisitState } from '../../types/visit';
import { useInputState } from '../../utils/inputState';
import { IClinicianOption } from '../clinicians/OptionalClinicianSelector';
import { CaptionLabel } from '../labels/CaptionLabel';
import { LiveViewStop } from '../mapping/GoogleMap/helpers/stopTypes';
import { HorizontalStack, VerticalStack } from '../utils/linearLayouts';
import { LiveCalendar } from './LiveCalendar';
import { LiveMap } from './LiveMap';
import { LiveViewClinicianSelector } from './LiveViewClinicianSelector';

export interface IClinicianAppointment {
    id: string;
    startDatetime: Date | null;
    endDatetime: Date | null;
    state: ClinicianAppointmentState;
    outcome: ClinicianAppointmentOutcome;
    clinician: IClinicianOption | null;
    durationMinutes: number;
}
interface IVisitFromQuery {
    id: string;
    timezone: string;
    outcome: VisitOutcome;
    state: VisitState;
    disciplines: {
        id: string;
        code: string;
    }[];
    address: {
        id: string;
        coordinates: {
            latitude: number;
            longitude: number;
        };
    };
    patient: {
        id: string;
        fullName: string;
    };
    clinicianAppointments: IClinicianAppointment[];
}

export interface ILiveViewCliniciansQuery {
    clinicians: {
        items: IClinicianOption[];
    };
}

export interface ILiveViewVisitsQuery {
    visits: {
        items: IVisitFromQuery[];
        totalCount: number;
        hasMore: boolean;
    };
}

const INITIAL_BATCH_SIZE = 50;

const LiveViewCliniciansQuery = gql`
    query LiveViewClinicians {
        clinicians {
            items {
                ...ClinicianOptionFragment
            }
        }
    }
`;

const LiveViewVisitsQuery = gql`
    query LiveViewVisits($date: Date!, $pagination: PaginationInput, $ordering: VisitOrdering) {
        visits(
            filters: {
                scheduledOrPlannedInDateWindow: { startDate: $date, endDate: $date }
                NOT: { state: { exact: UNSCHEDULED } }
            }
            pagination: $pagination
            ordering: $ordering
        ) {
            items {
                id
                outcome
                state
                address {
                    id
                    coordinates {
                        latitude
                        longitude
                    }
                }
                timezone
                disciplines {
                    id
                    code
                }
                patient {
                    id
                    fullName
                }
                clinicianAppointments {
                    id
                    startDatetime
                    endDatetime
                    outcome
                    state
                    durationMinutes
                    clinician {
                        ...ClinicianOptionFragment
                    }
                }
            }
            totalCount
            hasMore
        }
    }
`;

const mergeEntities = <T extends { id: string }>(existing: T[], newEntities: T[]): T[] => {
    const entityMap = new Map<string, T>();

    existing.forEach((entity) => {
        entityMap.set(entity.id, entity);
    });

    newEntities.forEach((entity) => {
        entityMap.set(entity.id, entity);
    });

    return Array.from(entityMap.values());
};

export interface IEvent {
    start: Date;
    end: Date;
    title: string;
    uuid: string;
    disciplines?: string[];
    patientName: string;
    /*  Represents the level of attention needed for the Visit/Appointment. */
    needsAttentionState: NeedsAttentionState;
    /*  Represents the status or outcome of the Visit/Appointment. */
    status: string;
    timezone: string;
    clinicianName: string;
    isOverflow?: boolean;
    hiddenEvents?: IEvent[];
    _horizontalIndex?: number;
    groupId?: string;
    durationMinutes: number;
}

export interface IClinicianAndVisitCount {
    clinician: IClinicianOption;
    visitCount: number;
}
export type CliniciansWithVisitCount = Record<string, IClinicianAndVisitCount>;
type EventsByClinician = Record<string, IEvent[]>;
type MapStopsByClinician = Record<string, LiveViewStop[]>;

export enum NeedsAttentionState {
    NEEDS_ATTENTION = 3,
    MAY_NEED_ATTENTION = 2,
    NO_ATTENTION_NEEDED = 1,
}

export enum EventStatus {
    UNASSIGNED = 'UNASSIGNED',
    CANCELED = 'CANCELED',
    ON_HOLD = 'ON_HOLD',
    COMPLETED = 'COMPLETED',
    STARTED = 'STARTED',
    SCHEDULED = 'SCHEDULED',
}

export const EVENT_STATE_MAIN_COLORS: Record<NeedsAttentionState, string> = {
    [NeedsAttentionState.NEEDS_ATTENTION]: '#EC221F',
    [NeedsAttentionState.MAY_NEED_ATTENTION]: '#FBBC04',
    [NeedsAttentionState.NO_ATTENTION_NEEDED]: '#14AE5C',
};

export const EVENT_STATE_BG_COLORS: Record<NeedsAttentionState, string> = {
    [NeedsAttentionState.NEEDS_ATTENTION]: '#FFDEDE',
    [NeedsAttentionState.MAY_NEED_ATTENTION]: '#FFEBCF',
    [NeedsAttentionState.NO_ATTENTION_NEEDED]: '#F4FCF5',
};

const TIME_FORMAT_OPTIONS = {
    hour: 'numeric' as const,
    minute: '2-digit' as const,
};

/**
 * Returns the LegendState for a given visit and clinician appointment, based on the mapping above.
 */
const transformStateToLegend = (
    clinicianAppointment: IClinicianAppointment,
): NeedsAttentionState => {
    /**
     * NEEDS_ATTENTION:
     * encompasses a) absence of clinician on an appointment, b) scheduler/agency gets word that patient
     * has cancelled and clinician does not yet know, c) other unanticipated 11th hour scenarios
     */

    if (!clinicianAppointment.clinician) {
        return NeedsAttentionState.NEEDS_ATTENTION;
    }

    /**
     * MAY_NEED_ATTENTION:
     * encompasses a)  ≧ 15 minutes overdue b) other unanticipated medium acuity scenarios
     */
    if (
        clinicianAppointment.startDatetime &&
        clinicianAppointment.state === ClinicianAppointmentState.NOT_STARTED
    ) {
        const diffMinutes = moment().diff(clinicianAppointment.startDatetime, 'minutes');
        if (diffMinutes >= 15) {
            return NeedsAttentionState.MAY_NEED_ATTENTION;
        }
    }
    /**
     * NO_ATTENTION_NEEDED
     */

    return NeedsAttentionState.NO_ATTENTION_NEEDED;
};

const getEventStatus = (
    visit: IVisitFromQuery,
    clinicianAppointment: IClinicianAppointment,
): EventStatus => {
    if (!clinicianAppointment.clinician) {
        return EventStatus.UNASSIGNED;
    }

    if (
        visit.outcome === VisitOutcome.PATIENT_CANCELED ||
        visit.outcome === VisitOutcome.PATIENT_LATE_CANCELED
    ) {
        return EventStatus.CANCELED;
    }

    if (visit.state === VisitState.IN_REVIEW) {
        return EventStatus.ON_HOLD;
    }

    if (clinicianAppointment.outcome === ClinicianAppointmentOutcome.COMPLETED) {
        return EventStatus.COMPLETED;
    }
    if (clinicianAppointment.state === ClinicianAppointmentState.AT_LOCATION) {
        return EventStatus.STARTED;
    }

    if (visit.state === VisitState.SCHEDULED) {
        return EventStatus.SCHEDULED;
    }

    return EventStatus.SCHEDULED;
};

const renderLegend = () => (
    <Box
        display={'flex'}
        flexDirection={'column'}
        width={'90%'}
        padding={'8px'}
        gap={'8px'}
        bgcolor={'white'}
        borderRadius={'4px'}
        boxShadow={'0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)'}
        position={'absolute'}
        bottom={'16px'}
        left={'20px'}
        zIndex={1}
        sx={{
            fontSize: '12px',
            lineHeight: '16px',
        }}
    >
        <Box display={'flex'} alignItems={'center'} gap={'8px'}>
            <Box
                bgcolor={EVENT_STATE_MAIN_COLORS[NeedsAttentionState.NEEDS_ATTENTION]}
                width={'12px'}
                height={'12px'}
                borderRadius={'50%'}
            />
            Needs Attention
        </Box>
        <Box display={'flex'} alignItems={'center'} gap={'8px'}>
            <Box
                bgcolor={EVENT_STATE_MAIN_COLORS[NeedsAttentionState.MAY_NEED_ATTENTION]}
                width={'12px'}
                height={'12px'}
                borderRadius={'50%'}
            />
            May Need Attention
        </Box>
        <Box display={'flex'} alignItems={'center'} gap={'8px'}>
            <Box
                bgcolor={EVENT_STATE_MAIN_COLORS[NeedsAttentionState.NO_ATTENTION_NEEDED]}
                width={'12px'}
                height={'12px'}
                borderRadius={'50%'}
            />
            No Attention Needed
        </Box>
    </Box>
);
export const unassignedClinician: IClinicianOption = {
    id: UNASSIGNED_QHP_ID,
    fullName: 'Unassigned',
    firstName: 'Not',
    lastName: 'Assigned',
    logoUrl: '',
    licenses: [],
};

export enum SELECTOR_GROUP {
    UNASSIGNED = 'unassigned',
    ASSIGNED = 'assigned',
}

const formatTimeRange = (startDate: Date, endDate: Date): string => {
    const startTime = startDate.toLocaleTimeString([], TIME_FORMAT_OPTIONS);
    const endTime = endDate.toLocaleTimeString([], TIME_FORMAT_OPTIONS);
    return `${startTime} - ${endTime}`;
};

const getEventFromVisit = (
    visit: IVisitFromQuery,
    clinicianAppointment: IClinicianAppointment,
): IEvent | undefined => {
    if (clinicianAppointment.startDatetime && clinicianAppointment.endDatetime) {
        return {
            start: clinicianAppointment.startDatetime,
            end: clinicianAppointment.endDatetime,
            title: formatTimeRange(
                clinicianAppointment.startDatetime,
                clinicianAppointment.endDatetime,
            ),
            uuid: clinicianAppointment.id,
            needsAttentionState: transformStateToLegend(clinicianAppointment),
            status: getEventStatus(visit, clinicianAppointment),
            clinicianName: clinicianAppointment.clinician?.fullName ?? 'Unassigned',
            patientName: visit.patient.fullName,
            timezone: visit.timezone,
            disciplines: visit.disciplines.map((discipline) => discipline.code),
            durationMinutes: clinicianAppointment.durationMinutes,
        };
    }
    return;
};

const transformVisits = (
    visits: IVisitFromQuery[],
    clinicians: IClinicianOption[],
): [EventsByClinician, CliniciansWithVisitCount, MapStopsByClinician] => {
    const eventsByClinician: EventsByClinician = {};
    const cliniciansByUUID: CliniciansWithVisitCount = {
        [unassignedClinician.id]: {
            clinician: unassignedClinician,
            visitCount: 0,
        },
    };
    const mapStopsByClinician: MapStopsByClinician = {};

    clinicians.forEach((clinician) => {
        cliniciansByUUID[clinician.id] = {
            clinician,
            visitCount: 0,
        };
        eventsByClinician[clinician.id] = [];
        mapStopsByClinician[clinician.id] = [];
    });

    visits.forEach((visit) => {
        visit.clinicianAppointments.forEach((clinicianAppointment) => {
            const clinician = clinicianAppointment.clinician;
            let clinicianUUID = UNASSIGNED_QHP_ID;
            if (clinician) {
                clinicianUUID = clinician.id;
            }

            if (!(clinicianUUID in cliniciansByUUID)) {
                cliniciansByUUID[clinicianUUID] = {
                    clinician: clinicianAppointment.clinician ?? unassignedClinician,
                    visitCount: 0,
                };
            }
            if (!(clinicianUUID in eventsByClinician)) {
                eventsByClinician[clinicianUUID] = [];
            }
            if (!(clinicianUUID in mapStopsByClinician)) {
                mapStopsByClinician[clinicianUUID] = [];
            }

            cliniciansByUUID[clinicianUUID].visitCount++;
            const event = getEventFromVisit(visit, clinicianAppointment);
            if (event) {
                eventsByClinician[clinicianUUID].push(event);
            }
            mapStopsByClinician[clinicianUUID].push({
                lat: visit.address.coordinates.latitude.toString(),
                lng: visit.address.coordinates.longitude.toString(),
                group_id: clinicianUUID,
                datetime: moment(clinicianAppointment.startDatetime),
                state: transformStateToLegend(clinicianAppointment),
                type: 'liveView',
            });
        });
    });

    const cliniciansWithVisits = Object.values(cliniciansByUUID)
        .filter((c) => c.visitCount > 0)
        .sort((a, b) =>
            a.clinician.lastName.localeCompare(b.clinician.lastName, undefined, {
                sensitivity: 'base',
            }),
        );
    const cliniciansWithoutVisits = Object.values(cliniciansByUUID)
        .filter((c) => c.visitCount === 0)
        .sort((a, b) =>
            a.clinician.lastName.localeCompare(b.clinician.lastName, undefined, {
                sensitivity: 'base',
            }),
        );
    const sortedCliniciansArray = [...cliniciansWithVisits, ...cliniciansWithoutVisits];

    const sortedCliniciansByUUID = sortedCliniciansArray.reduce<
        Record<string, (typeof sortedCliniciansArray)[number]>
    >((acc, curr) => {
        acc[curr.clinician.id] = curr;
        return acc;
    }, {});
    return [eventsByClinician, sortedCliniciansByUUID, mapStopsByClinician];
};

export const LiveView = () => {
    const [isCalendarView, setIsCalendarView] = useState(true);
    const [viewType, setViewType] = useState<'all' | 'clinician'>('all');

    const { date, dateLabel } = useDashboardPageContext();

    const { data: firstBatchData } = useSuspenseQuery<ILiveViewVisitsQuery>(LiveViewVisitsQuery, {
        variables: {
            date: GraphInputHelpers.date(date),
            pagination: { limit: INITIAL_BATCH_SIZE, offset: 0 },
            ordering: { startDatetime: Ordering.ASC },
        },
        fetchPolicy: 'cache-and-network',
    });

    const { data: cliniciansData, loading: cliniciansLoading } = useQuery<ILiveViewCliniciansQuery>(
        LiveViewCliniciansQuery,
        {
            fetchPolicy: 'cache-and-network',
        },
    );

    const { data: remainingVisitsData, loading: remainingVisitsLoading } =
        useQuery<ILiveViewVisitsQuery>(LiveViewVisitsQuery, {
            variables: {
                date: GraphInputHelpers.date(date),
                pagination: {
                    limit: Math.max(0, firstBatchData.visits.totalCount - INITIAL_BATCH_SIZE),
                    offset: INITIAL_BATCH_SIZE,
                },
                ordering: { startDatetime: Ordering.ASC },
            },
            fetchPolicy: 'cache-and-network',
            skip: !firstBatchData.visits.hasMore,
        });

    const allVisits = useMemo(() => {
        const firstBatch = firstBatchData.visits.items;
        const remaining = remainingVisitsData?.visits.items ?? [];
        return mergeEntities<IVisitFromQuery>(firstBatch, remaining);
    }, [firstBatchData.visits.items, remainingVisitsData?.visits.items]);

    const allClinicians = useMemo(() => {
        const [, cliniciansFromVisits] = transformVisits(allVisits, []);
        const visitClinicians = Object.values(cliniciansFromVisits).map((item) => item.clinician);

        const queryClinicians = cliniciansData?.clinicians.items ?? [];
        return mergeEntities<IClinicianOption>(visitClinicians, queryClinicians);
    }, [allVisits, cliniciansData?.clinicians.items]);

    const isLoadingMore = cliniciansLoading || remainingVisitsLoading;

    const [eventsByClinician, cliniciansByUUID, mapStopsByClinician] = useMemo(
        () => transformVisits(allVisits, allClinicians),
        [allVisits, allClinicians],
    );
    const inputState = useInputState<IClinicianAndVisitCount | null>(null, { required: false });
    const numberOfClinicians = Object.values(cliniciansByUUID).filter(
        (clinician) => clinician.visitCount > 0,
    ).length;
    const filteredEvents = useMemo(
        () =>
            inputState.value
                ? eventsByClinician[inputState.value.clinician.id]
                : Object.values(eventsByClinician).flat(),
        [inputState.value, eventsByClinician],
    );
    const filteredMapStopsByClinician = useMemo(
        () =>
            inputState.value
                ? mapStopsByClinician[inputState.value.clinician.id]
                : Object.values(mapStopsByClinician).flat(),
        [inputState.value, mapStopsByClinician],
    );
    const totalEventCount = useMemo(
        () => Object.values(eventsByClinician).flat().length,
        [eventsByClinician],
    );

    return (
        <>
            <VerticalStack height={'100vh'} position={'relative'}>
                <Box bgcolor={'white'} p={2}>
                    <CaptionLabel>{dateLabel}</CaptionLabel>
                    <Box>
                        <Typography variant={'h6'}>
                            {isCalendarView ? 'Calendar ' : 'Map '}Live View
                        </Typography>
                    </Box>
                    {isLoadingMore && (
                        <HorizontalStack gap={1}>
                            <CircularProgress size={14} />
                            <Typography variant='caption'>
                                Loading more visits and clinicians...
                            </Typography>
                        </HorizontalStack>
                    )}
                    <Box display={'flex'} justifyContent={'center'}>
                        <ToggleButtonGroup
                            color='primary'
                            value={viewType}
                            fullWidth
                            exclusive
                            onChange={(_, value: string | null) => {
                                if (value) {
                                    setViewType(value as 'all' | 'clinician');
                                    inputState.reset();
                                }
                            }}
                            aria-label='All Visits Or By Clinician'
                            sx={{
                                margin: '16px 0',
                            }}
                        >
                            <ToggleButton disabled={viewType === 'all'} value='all'>
                                All Visits
                            </ToggleButton>
                            <ToggleButton disabled={viewType === 'clinician'} value='clinician'>
                                By Clinician
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                    {viewType === 'all' && (
                        <HorizontalStack
                            justifyContent={'space-between'}
                            sx={{ fontSize: '14px', lineHeight: '20px' }}
                        >
                            <Typography variant={'inherit'}>
                                <Typography
                                    component={'span'}
                                    variant={'inherit'}
                                    fontWeight={'bold'}
                                >
                                    {`${numberOfClinicians} `}
                                </Typography>
                                Clinicians with visits
                            </Typography>
                            <Typography variant={'inherit'}>
                                <Typography
                                    component={'span'}
                                    variant={'inherit'}
                                    fontWeight={'bold'}
                                >
                                    {`${totalEventCount} `}
                                </Typography>
                                Visits
                            </Typography>
                        </HorizontalStack>
                    )}
                    {viewType === 'clinician' && (
                        <LiveViewClinicianSelector
                            inputState={inputState}
                            options={Object.values(cliniciansByUUID).map((clinician) => clinician)}
                            groupBy={(clinician) =>
                                cliniciansByUUID[clinician.clinician.id].visitCount === 0
                                    ? SELECTOR_GROUP.UNASSIGNED
                                    : SELECTOR_GROUP.ASSIGNED
                            }
                        />
                    )}
                </Box>
                <Box bgcolor={'#F5F5F5'} p={isCalendarView ? 2 : 0} position={'relative'}>
                    <Box
                        display={'inline-flex'}
                        alignItems={'flex-start'}
                        flexDirection={'row-reverse'}
                        bgcolor={'white'}
                        borderRadius={'4px'}
                        position={'absolute'}
                        zIndex={2}
                        top={'10px'}
                        left={'10px'}
                        padding={'6px 8px'}
                        boxShadow={
                            '0 1px 2px 0 rgba(0, 0, 0, 0.30), 0 1px 3px 1px rgba(0, 0, 0, 0.15)'
                        }
                        onClick={() => {
                            setIsCalendarView(!isCalendarView);
                        }}
                        sx={{
                            cursor: 'pointer',
                            ':hover > *': {
                                color: AXLE_TEAL,
                            },
                        }}
                    >
                        <Typography
                            variant={'button'}
                            color={'#0062FF'}
                            sx={{
                                lineHeight: '20px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                            }}
                        >
                            {isCalendarView ? 'Map ' : 'Calendar '}
                            {isCalendarView ? <Room /> : <CalendarToday />}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    flex={'1 1 0%'}
                    bgcolor={'#F5F5F5'}
                    p={isCalendarView ? 2 : 0}
                    sx={{ overflowY: 'auto' }}
                >
                    {isCalendarView ? (
                        <Box marginTop={'16px'} mb={'80px'}>
                            <LiveCalendar
                                events={filteredEvents}
                                backgroundEvents={[]}
                                isClinicianView={viewType === 'clinician' && !!inputState.value}
                            />
                        </Box>
                    ) : (
                        <Box style={{ height: '100%' }}>
                            <LiveMap
                                stops={filteredMapStopsByClinician}
                                selectedClinician={inputState.value?.clinician ?? null}
                                drawRoutes={!!inputState.value}
                            />
                        </Box>
                    )}
                </Box>
                {renderLegend()}
            </VerticalStack>
        </>
    );
};
